import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Row } from 'reactstrap';
import { AutobizIcon, AutobizSectionTitle, AutobizBanner, AutobizPopup } from 'autobiz-strap';
import { t } from 'autobiz-translate';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import Markdown from 'markdown-to-jsx';
import Footer from '../Footer/Footer';
import Header from '../Header/Header';

import ProductCard from '../ProductCard/ProductCard';
import { allProducts, OfficeCountry } from '../../../../hexagon/config/appConfig';
import { ProductName, Products } from '../../../../hexagon/models/productModel';
import UserFeeds from '../UserFeeds/UserFeeds';
import { getUserFeedsSelector } from '../../view-models-generators/userFeedsSelectors';
import UserStats from '../UserStats/UserStats';
import { getUserSelector } from '../../view-models-generators/userSelector';
import { getLinkLanguage } from '../../../../language-context';
import PopUpWebinar from '../PopupWebinar/PopupWebinar';
import AuthSpinner from '../AuthSpinner/AuthSpinner';
import ZohoSurvey from '../ZohoSurvey/ZohoSurvey';
import PopUpMarket from '../PopupMarket/PopUpMarket';
import { getBannerUseCase } from '../../../../hexagon/usecases/getBanner/getBannerUseCase';
import { getBannerSelector } from '../../view-models-generators/bannerSelectors';
import { getPopupUseCase } from '../../../../hexagon/usecases/getPopup/getPopupUseCase';
import { getPopupSelector } from '../../view-models-generators/popupSelectors';
import { useHybridAuth } from '../AuthProvider/HybdridAuthProvider';

const difference = (arr1: ProductName[], arr2: ProductName[]) =>
    arr1.filter((x: ProductName) => !arr2.includes(x));

const Dashboard: React.FC = () => {
    const dispatch = useDispatch();
    const { authMode } = useHybridAuth();

    const { count: actionCount, data } = useSelector(getUserFeedsSelector);
    const { user } = useSelector(getUserSelector);
    let userProducts = user?.userApps ? user?.userApps.map((obj: any) => obj.name) : [];
    const {
        data: bannerData,
        status: bannerStatus,
        exists: bannerExists,
    } = useSelector(getBannerSelector);

    const {
        data: popupData,
        status: popupStatus,
        exists: popupExists,
    } = useSelector(getPopupSelector);

    const productsWithoutOffice = user?.userApps
        ? user?.userApps.filter((p: any) => p.name !== 'autobizOffice')
        : [];

    const discoverableProducts = [] as Products;
    userProducts = userProducts.map((product: any) =>
        product === 'autobizMarket_v2' ? 'autobizMarket' : product,
    );
    difference(allProducts, userProducts).forEach((p) => {
        discoverableProducts.push({
            name: p,
            link: `https://office.autobiz.com/${getLinkLanguage(user.language)}/demo/`,
        });
    });

    useEffect(() => {
        if (authMode === 'auth0' && user.language && user.country) {
            if (popupStatus === 'idle') {
                dispatch(getPopupUseCase(user.language, user.country as OfficeCountry));
            }

            if (bannerStatus === 'idle') {
                dispatch(getBannerUseCase(user.language, user.country as OfficeCountry));
            }
        }
    }, [dispatch, user.language, user.country, popupStatus, bannerStatus, authMode]);
    return (
        <>
            <Header />
            <Container className="hasFixedHeader" />

            <PopUpMarket country={user?.country} provider={user?.provider} />

            <PopUpWebinar {...user} />
            <ZohoSurvey user={user} />
            {popupStatus === 'succeeded' && popupExists && (
                <AutobizPopup
                    title={popupData.title}
                    ctaLabel={popupData.cta.label}
                    ctaHref={popupData.cta.href}
                    ctaCloseLabel={popupData.ctaClose.label}
                >
                    <Markdown>{popupData.content}</Markdown>
                </AutobizPopup>
            )}
            {bannerStatus === 'succeeded' && bannerExists && (
                <AutobizBanner
                    textColor={bannerData.textColor}
                    backgroundColor={bannerData.backgroundColor}
                    align="center"
                >
                    <Markdown>{bannerData.content}</Markdown>
                </AutobizBanner>
            )}
            <Container>
                {user.lastName && (
                    <div className="lead mt-5">
                        {t('hello')} {user.firstName} {user.lastName}
                        {user.provider === 'AZ' && (
                            <>
                                {Object.keys(data).length > 0 && (
                                    <>
                                        , {t('you_have')}{' '}
                                        <span className="text-primary font-weight-bold">
                                            {actionCount}
                                        </span>{' '}
                                        {t('action_to_perform')}
                                    </>
                                )}{' '}
                            </>
                        )}
                    </div>
                )}

                <UserFeeds />
                <UserStats />

                {productsWithoutOffice.length > 0 && (
                    <div className="mt-4">
                        <AutobizSectionTitle>
                            <AutobizIcon name="Grid" /> {t('your_subscribed_products')}
                        </AutobizSectionTitle>
                        <Row>
                            {productsWithoutOffice.map((product: any) => (
                                <ProductCard key={product.name} product={product} type="connect" />
                            ))}
                        </Row>
                    </div>
                )}
                {user.provider === 'AZ' && (
                    <div className="mt-5">
                        <AutobizSectionTitle>
                            <AutobizIcon name="Cloud" /> {t('discover_other_products')}
                        </AutobizSectionTitle>
                        <Row>
                            {discoverableProducts.map((product) => (
                                <ProductCard key={product.name} product={product} type="demo" />
                            ))}
                        </Row>
                    </div>
                )}
                <Footer />
            </Container>
        </>
    );
};

export default Dashboard;
